import { z } from 'zod';
import type { SmriqDurationDefaults } from '../services/smriqPlanConfig';
import { parseDurationOptions, type SmriqPlanDefaultsForm } from '../utils/smriqPlanDefaults';

const WHOLE_NUMBER = /^\d+$/;

const optionalWholeNumber = z
    .string()
    .trim()
    .refine((v) => v === '' || WHOLE_NUMBER.test(v), 'Enter a whole number')
    .refine((v) => v === '' || Number(v) >= 1, 'Must be at least 1');

const planDefaultsObject = z.object({
    duration_mode: z.enum(['defaults', 'type', 'dropdown', 'fixed']),
    duration_default: optionalWholeNumber,
    duration_min: optionalWholeNumber,
    duration_max: optionalWholeNumber,
    duration_options: z.string(),
    mot_default: z.enum(['', 'yes', 'no']),
    allow_mot_selection: z.boolean(),
    pricing_variant: z.enum(['oem_approved', 'division_approved']),
});

/** `undefined` for a blank field, `null` for one the field rules already rejected. */
const parseMonths = (raw: string): number | undefined | null => {
    const token = raw.trim();
    if (token === '') return undefined;
    return WHOLE_NUMBER.test(token) && Number(token) >= 1 ? Number(token) : null;
};

const addIssue = (ctx: z.RefinementCtx, path: keyof SmriqPlanDefaultsForm, message: string): void => {
    ctx.addIssue({ code: 'custom', path: [path], message });
};

/**
 * Typed Range is validated against the bounds the save RESOLVES to, not the typed ones:
 * SmriqPlanConfigResolver substitutes the platform range for a blank min or max, so a pair
 * that reads coherent here can invert there (the product range is then discarded) or put the
 * default outside the effective window (the default is then dropped). Both are silent.
 */
const addTypedRangeIssues = (
    value: SmriqPlanDefaultsForm,
    platform: SmriqDurationDefaults | undefined,
    ctx: z.RefinementCtx,
): void => {
    const min = parseMonths(value.duration_min);
    const max = parseMonths(value.duration_max);
    const def = parseMonths(value.duration_default);

    if (min === null || max === null || def === null) return;

    if (min !== undefined && max !== undefined) {
        if (min > max) {
            addIssue(ctx, 'duration_max', 'Maximum must be at least the minimum');
            return;
        }
        if (def !== undefined && (def < min || def > max)) {
            addIssue(ctx, 'duration_default', `Default must be between ${min} and ${max} months`);
        }
        return;
    }

    if (min === undefined && max === undefined && def === undefined) return;

    if (!platform) {
        addIssue(
            ctx,
            min === undefined ? 'duration_min' : 'duration_max',
            'Enter both a minimum and a maximum while the platform range is unavailable',
        );
        return;
    }

    const effectiveMin = min ?? platform.min;
    const effectiveMax = max ?? platform.max;

    if (effectiveMin > effectiveMax) {
        if (min !== undefined) {
            addIssue(ctx, 'duration_min', `Minimum must be at most ${platform.max} months, the platform maximum`);
        } else {
            addIssue(ctx, 'duration_max', `Maximum must be at least ${platform.min} months, the platform minimum`);
        }
        return;
    }

    if (def !== undefined && (def < effectiveMin || def > effectiveMax)) {
        addIssue(ctx, 'duration_default', `Default must be between ${effectiveMin} and ${effectiveMax} months`);
    }
};

const addDropdownIssues = (value: SmriqPlanDefaultsForm, ctx: z.RefinementCtx): void => {
    const options = parseDurationOptions(value.duration_options);
    const def = parseMonths(value.duration_default);

    if (options === null) {
        addIssue(ctx, 'duration_options', 'Enter whole months separated by commas');
        return;
    }
    if (options.length === 0) {
        addIssue(ctx, 'duration_options', 'Add at least one duration');
        return;
    }
    if (def != null && !options.includes(def)) {
        addIssue(ctx, 'duration_default', 'Default must be one of the options');
    }
};

/**
 * Built from the LIVE platform bounds (useSmriqDurationDefaults) rather than a module-level
 * constant, so the Typed Range rules see the same fallback the backend resolver applies.
 */
export const makeSmriqPlanDefaultsSchema = (platform: SmriqDurationDefaults | undefined) =>
    planDefaultsObject.superRefine((value, ctx) => {
        if (value.duration_mode === 'type') addTypedRangeIssues(value, platform, ctx);

        if (value.duration_mode === 'dropdown') addDropdownIssues(value, ctx);

        if (value.duration_mode === 'fixed' && value.duration_default.trim() === '') {
            addIssue(ctx, 'duration_default', 'Enter the fixed duration');
        }

        if (!value.allow_mot_selection && value.mot_default === '') {
            addIssue(ctx, 'allow_mot_selection', 'Set an MOT default before hiding the control');
        }
    });
